/**
 * Deep linking support for timeline navigation
 *
 * Allows users to link directly to specific timeline items via URL parameters.
 * Implements browser back/forward history integration.
 */

import { timelineRegistry } from '../shared/state.js';

/**
 * Handle deep link URL parameters and navigate to specific timeline item
 *
 * Looks for URL parameters:  *  - ?id=nodeId - ID of the timeline item to show
 *  - ?timeline=timelineId - Specific timeline (optional, for multiple timelines)
 *
 * If found, scrolls timeline into view and highlights the item.
 *
 * @param {string} containerSelector - CSS selector for fallback timeline container
 * @returns {void}
 */
export function handleDeepLinking(containerSelector) {
  const urlParams = new URLSearchParams(window.location.search);
  const timelineId = urlParams.get('timeline');
  const nodeId = urlParams.get('id');
  if (!nodeId) return;

  let targetContainer;
  if (timelineId) {
    targetContainer = document.getElementById(timelineId);
  } else {
    targetContainer = document.querySelector(containerSelector);
  }

  if (!targetContainer) {
    console.warn('Timeline not found for deep linking:', timelineId || containerSelector);
    return;
  }

  // Avoid smooth container scrolling for vertical timelines to prevent overriding node scroll.
  const isHorizontal = targetContainer.classList.contains('timeline--horizontal');
  if (isHorizontal) {
    targetContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  let targetNode = targetContainer.querySelector('[data-node-id="' + nodeId + '"]');
  // Fallback: inline demos may use element id attributes (id="10") instead of data-node-id
  if (!targetNode) {
    // try by element id and ensure it's a descendant of the target container
    try {
      const byId = document.getElementById(nodeId);
      if (byId && targetContainer.contains(byId)) targetNode = byId;
    } catch (e) {
      // ignore invalid id selectors
    }
  }

  if (targetNode) {
    setTimeout(function() {
      // Ensure only one active item is highlighted in this timeline
      targetContainer.querySelectorAll('.timeline__item--active').forEach((n) => n.classList.remove('timeline__item--active'));
      targetNode.classList.add('timeline__item--active');
      const items = Array.from(targetNode.parentNode.children || []);
      const itemIndex = items.indexOf(targetNode);

      // Horizontal timelines: delegate to the engine for precision scrolling
      if (targetContainer.classList.contains('timeline--horizontal')) {
        navigateToNodeIndex(targetContainer, itemIndex);
        return;
      }

      // Vertical timelines: prefer native anchor scrolling
      try {
        if (!targetNode.id) {
          targetNode.id = String(nodeId);
        }
      } catch (e) {
        /* ignore */
      }

      try {
        // Use native anchor behavior to leverage browser scroll handling
        if (window.location && window.location.hash !== '#' + String(nodeId)) {
          window.location.hash = String(nodeId);
        }
      } catch (e) {
        /* ignore */
      }

      // Extra guard: ensure it is visible even if hash doesn't scroll
      try {
        targetNode.scrollIntoView({ behavior: 'auto', block: 'start' });
      } catch (e) {
        /* ignore */
      }
    }, 0);
  }
}

/**
 * Navigate timeline to a specific item by index
 *
 * Scrolls the timeline to display the item at the given index and updates the URL.
 * For horizontal timelines, uses horizontal scroll. For vertical, uses IntersectionObserver.
 *
 * @param {HTMLElement} container - Timeline container element
 * @param {number} index - Zero-based index of item to navigate to
 * @returns {void}
 */
export function navigateToNodeIndex(container, index) {
  if (!container) return;
  const timelineId = container.id || container.getAttribute('data-timeline-id');
  if (!timelineId) {
    console.warn('Cannot navigate: timeline container has no ID');
    return;
  }

  const tlData = timelineRegistry[timelineId];
  if (!tlData) {
    console.warn('Timeline not found in registry:', timelineId);
    return;
  }

  if (!container.classList.contains('timeline--horizontal')) {
    return;
  }

  if (tlData.setCurrentIndex && tlData.updatePosition) {
    tlData.setCurrentIndex(index);
    tlData.updatePosition();
  }
}
